import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { confirmAction } from "@/components/ui/confirm-dialog";

/**
 * Warns before the user leaves a page with unsaved changes: in-app navigation shows the shared
 * confirm dialog, and reload/close shows the browser's own prompt.
 *
 * Call `allowNavigation()` right before navigating away after a successful save, so the redirect
 * is not blocked. Changes that only touch the query string (tabs, filters) are never blocked.
 */
export function useUnsavedChangesGuard(isDirty: boolean) {
	const { t } = useTranslation();
	const allowedRef = useRef(false);
	const dirtyRef = useRef(isDirty);
	dirtyRef.current = isDirty;

	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			dirtyRef.current && !allowedRef.current && currentLocation.pathname !== nextLocation.pathname,
	);

	const blockerRef = useRef(blocker);
	blockerRef.current = blocker;

	useEffect(() => {
		if (blocker.state !== "blocked") return undefined;
		let active = true;
		confirmAction(t("common.unsavedChanges"), { confirmLabel: t("common.discardChanges") }).then(confirmed => {
			if (!active) return;
			if (confirmed) blockerRef.current.proceed?.();
			else blockerRef.current.reset?.();
		});
		return () => {
			active = false;
		};
	}, [blocker.state, t]);

	useEffect(() => {
		if (!isDirty) return undefined;
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			// Required by some browsers to show the prompt; the text itself is not displayed.
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [isDirty]);

	const allowNavigation = useCallback(() => {
		allowedRef.current = true;
	}, []);

	return { allowNavigation };
}

/**
 * Tracks whether `value` differs from its state when the page finished loading. The baseline is taken
 * on the first render where `ready` is true; call `markSaved()` after saving on pages that stay open.
 */
export function useIsDirty(value: unknown, ready = true) {
	const serialized = JSON.stringify(value ?? null);
	const baselineRef = useRef<string | null>(null);
	const resetPendingRef = useRef(false);
	const [, setVersion] = useState(0);

	// After markSaved(), the next render's value (e.g. the record re-fetched after saving) is the new baseline.
	if (resetPendingRef.current) {
		baselineRef.current = serialized;
		resetPendingRef.current = false;
	}
	if (ready && baselineRef.current === null) baselineRef.current = serialized;

	const markSaved = useCallback(() => {
		resetPendingRef.current = true;
		setVersion(v => v + 1);
	}, []);

	const isDirty = ready && baselineRef.current !== null && baselineRef.current !== serialized;
	return { isDirty, markSaved };
}
